import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Dependency health (part 165). Every value on this page is a live probe result: the API
// runs the checks on each request and never caches them, so a refresh is a real check.

type DepStatus = 'ok' | 'degraded' | 'down' | 'unknown' | 'circuit_open'
type BreakerState = 'closed' | 'open' | 'half_open'

interface Breaker {
  state: BreakerState
  failures: number
  fail_threshold: number
  trips: number
  opened_at?: string
  cooldown_until?: string
  last_error?: string
  last_error_at?: string
}

interface Dependency {
  name: string
  kind: string
  status: DepStatus
  critical: boolean
  latency_ms: number
  detail?: string
  error?: string
  meta?: Record<string, unknown>
  breaker?: Breaker
  checked_at: string
}

interface HealthView {
  service: string
  status: DepStatus
  checked_at: string
  duration_ms: number
  summary: { ok: number; degraded: number; down: number; unknown: number; circuit_open: number }
  dependencies: Dependency[]
}

const statusLabel: Record<DepStatus, string> = {
  ok: 'ปกติ',
  degraded: 'เสื่อมประสิทธิภาพ',
  down: 'ไม่พร้อมใช้งาน',
  unknown: 'ยังไม่ทราบ',
  circuit_open: 'ตัดวงจร',
}

const statusSev: Record<DepStatus, string> = {
  ok: 'sev-low',
  degraded: 'sev-medium',
  circuit_open: 'sev-high',
  down: 'sev-critical',
  unknown: '',
}

const statusTone: Record<DepStatus, string> = {
  ok: 'var(--color-emerald-soft)',
  degraded: 'var(--color-gold)',
  circuit_open: 'var(--color-amber)',
  down: 'var(--color-crimson)',
  unknown: 'var(--color-ivory-dim)',
}

const breakerLabel: Record<BreakerState, string> = {
  closed: 'ปิดวงจร · เรียกได้',
  open: 'เปิดวงจร · ตัดการเรียก',
  half_open: 'ครึ่งวงจร · ทดลอง',
}

const breakerTone: Record<BreakerState, string> = {
  closed: 'var(--color-emerald-soft)',
  open: 'var(--color-crimson)',
  half_open: 'var(--color-amber)',
}

const kindLabel: Record<string, string> = {
  postgres: 'ฐานข้อมูล',
  object_store: 'พื้นที่จัดเก็บไฟล์',
  http: 'บริการ HTTP',
  check: 'ตัวตรวจสอบ',
}

const detailLabel: Record<string, string> = {
  timeout: 'หมดเวลา',
  probe_failed: 'ตรวจไม่ผ่าน',
  not_configured: 'ยังไม่ตั้งค่า',
  circuit_open: 'ตัวตัดวงจรเปิดอยู่',
  sentinel_absent: 'ไม่พบไฟล์ตรวจสอบ',
  slow_queries: 'มีคำสั่งช้า',
  skipped: 'ข้ามการตรวจ',
}

const depLabel: Record<string, string> = {
  database: 'ฐานข้อมูลหลัก',
  object_storage: 'พื้นที่จัดเก็บไฟล์',
  object_store_edge: 'ช่องเชื่อมต่อพื้นที่จัดเก็บ',
}

function StatusDot({ status }: { status: DepStatus }) {
  const sev = statusSev[status]
  if (!sev) {
    return (
      <span className="row" style={{ gap: 7, color: statusTone[status], fontSize: 12.5 }}>
        <i className="sev-dot" style={{ background: statusTone[status] }} />
        {statusLabel[status]}
      </span>
    )
  }
  return (
    <span className={`sev ${sev}`}>
      <i className="sev-dot" />
      {statusLabel[status]}
    </span>
  )
}

function BreakerBadge({ breaker }: { breaker?: Breaker }) {
  if (!breaker) return <span className="dim">—</span>
  const tone = breakerTone[breaker.state]
  const until = breaker.state === 'open' && breaker.cooldown_until
    ? new Date(breaker.cooldown_until).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
    : null
  return (
    <span className="badge" style={{ color: tone, borderColor: tone }}
      title={`ล้มเหลวติดกัน ${breaker.failures}/${breaker.fail_threshold} · เปิดวงจร ${breaker.trips} ครั้ง`}>
      {breakerLabel[breaker.state]}
      {until ? ` · ถึง ${until}` : ''}
    </span>
  )
}

function metaLine(dep: Dependency): string | null {
  if (!dep.meta) return null
  if (dep.kind === 'postgres') {
    const slow = Number(dep.meta.slow_queries ?? 0)
    const conns = dep.meta.connections as { acquired?: number; max?: number } | undefined
    return `คำสั่งช้า ${slow} รายการ · การเชื่อมต่อ ${conns?.acquired ?? 0}/${conns?.max ?? 0}`
  }
  if (dep.kind === 'object_store') {
    return `แบ็กเอนด์ ${String(dep.meta.backend ?? '—')} · ไฟล์ตรวจสอบ ${String(dep.meta.object_key ?? '—')}`
  }
  return null
}

export function Health() {
  const q = useQuery({
    queryKey: ['health', 'dependencies'],
    queryFn: () => api<HealthView>('/v1/health/dependencies'),
    // The endpoint never caches, so neither does the client. Poll every 30 seconds.
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: 0,
  })

  const view = q.data
  const deps = view?.dependencies ?? []
  const needsAttention = deps.filter((d) => d.status === 'down' || d.status === 'circuit_open' || d.status === 'degraded').length
  const overall: DepStatus = view?.status ?? 'unknown'
  const lastChecked = view ? thaiDateTime(view.checked_at) : null

  return (
    <>
      <PageHead
        title="สุขภาพระบบ"
        sub="ตรวจการพึ่งพาภายนอกแบบสดทุกครั้งที่เรียก ไม่มีการแคช · รีเฟรชอัตโนมัติทุก 30 วินาที"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {lastChecked ? `ตรวจล่าสุด ${lastChecked}` : 'ยังไม่มีการตรวจ'}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => void q.refetch()} disabled={q.isFetching}>
              {q.isFetching ? 'กำลังตรวจ…' : 'ตรวจใหม่'}
            </button>
          </div>
        }
      />

      {q.isLoading ? (
        <div className="stack"><Skel h={120} /><Skel h={260} /></div>
      ) : q.isError ? (
        <Empty
          title="ตรวจสอบสถานะไม่สำเร็จ"
          body={q.error instanceof Error ? q.error.message : 'เรียกข้อมูลสุขภาพระบบไม่สำเร็จ กรุณาลองใหม่'}
        />
      ) : !view ? (
        <Empty title="ไม่มีข้อมูลสถานะ" body="ยังไม่ได้รับผลการตรวจจากเซิร์ฟเวอร์" />
      ) : (
        <>
          {view.status !== 'ok' ? (
            <div className="card card-pad" style={{ marginBottom: 18, borderColor: statusTone[overall] }}>
              <div className="row" style={{ gap: 12 }}>
                <i className="sev-dot" style={{ width: 9, height: 9, background: statusTone[overall] }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {view.status === 'down' ? 'ระบบไม่พร้อมให้บริการ' : 'ระบบทำงานไม่เต็มกำลัง'}
                  </div>
                  <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>
                    {needsAttention > 0
                      ? `มีการพึ่งพาที่ต้องดูแล ${needsAttention} รายการ จากทั้งหมด ${deps.length} รายการ`
                      : `ไม่มีการพึ่งพาที่ล้มเหลว แต่ยังตรวจยืนยันบางรายการไม่ได้ (${deps.length} รายการ)`}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid-4 stagger">
            <Stat label="สถานะโดยรวม" value={statusLabel[overall]} unit={`· ${view.service}`}
              foot={`ใช้เวลา ${view.duration_ms} ms ต่อการตรวจหนึ่งรอบ`} />
            <Stat label="การพึ่งพาที่ปกติ" value={view.summary.ok} unit={`/ ${deps.length}`}
              foot={`ตรวจเมื่อ ${thaiDateTime(view.checked_at)}`} />
            <Stat label="ต้องดูแล" value={needsAttention} unit="รายการ"
              foot={`ไม่พร้อมใช้งาน ${view.summary.down} · ตัดวงจร ${view.summary.circuit_open} · เสื่อมสภาพ ${view.summary.degraded}`} />
            <Stat label="ยังยืนยันไม่ได้" value={view.summary.unknown} unit="รายการ"
              foot="ไม่นับเป็นความล้มเหลว แต่ยังไม่ใช่หลักฐานว่าระบบปกติ" />
          </div>

          <div className="table-wrap card" style={{ marginTop: 18 }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 260 }}>การพึ่งพา</th>
                  <th style={{ width: 140 }}>ประเภท</th>
                  <th style={{ width: 180 }}>สถานะ</th>
                  <th style={{ width: 110 }} className="num">เวลาตอบสนอง</th>
                  <th style={{ width: 210 }}>ตัวตัดวงจร</th>
                  <th>ข้อผิดพลาดล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {deps.map((dep) => {
                  const meta = metaLine(dep)
                  const lastError = dep.error || dep.breaker?.last_error || ''
                  return (
                    <tr key={dep.name} style={{ cursor: 'default' }}>
                      <td>
                        <div className="row" style={{ gap: 8 }}>
                          <span>{depLabel[dep.name] ?? dep.name}</span>
                          {dep.critical ? <span className="badge badge-received">จำเป็น</span> : null}
                        </div>
                        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                          {meta ?? dep.name}
                        </div>
                      </td>
                      <td className="dim">{kindLabel[dep.kind] ?? dep.kind}</td>
                      <td>
                        <StatusDot status={dep.status} />
                        {dep.detail ? (
                          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                            {detailLabel[dep.detail] ?? dep.detail}
                          </div>
                        ) : null}
                      </td>
                      <td className="num tnum">{dep.latency_ms} ms</td>
                      <td><BreakerBadge breaker={dep.breaker} /></td>
                      <td style={{ maxWidth: 380 }}>
                        {lastError ? (
                          <span className="mono" title={lastError}
                            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-crimson)', fontSize: 11.5 }}>
                            {lastError}
                          </span>
                        ) : (
                          <span className="dim">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.7 }}>
            ตัวตัดวงจรจะเปิดเมื่อการเรียกหนึ่งรายการล้มเหลวติดกัน {view.dependencies[0]?.breaker?.fail_threshold ?? 5} ครั้ง
            แล้วหยุดเรียกเป็นการชั่วคราว 30 วินาที ก่อนปล่อยหนึ่งคำขอเพื่อทดลองว่า dependency กลับมาแล้วหรือไม่ ·
            สถานะ “ยังไม่ทราบ” คือตรวจไม่ได้โดยไม่ใช่ความผิดพลาด เช่น พื้นที่จัดเก็บแบบโลคอลที่ยังไม่มีไฟล์ตรวจสอบ
          </div>
        </>
      )}
    </>
  )
}
